import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiQuery } from '../../api/hooks';
import type { AppointmentSummaryResponse, SalesSummaryResponse } from '../../api/types';
import { Columns, type Column } from '../../components/charts/Columns';
import { OutcomeLegend } from '../../components/charts/OutcomeBars';
import { OUTCOME_COLOURS } from '../../components/charts/outcomes';
import { bucketDays, percent } from '../../components/charts/buckets';
import { bucketLabel, bucketTitle } from '../../components/charts/dates';
import { Icon } from '../../components/Icon';
import { compact, pounds } from '../../i18n/format';
import { magnitude } from '../catalog/money';
import { shiftDay } from '../scheduling/clinicTime';

/** Days back the appointment and sales columns cover, today included. */
const DAYS_BACK = 14;
/** Days ahead the appointment columns cover: what is already booked. */
const DAYS_AHEAD = 7;
/** Weeks the no-show trend covers, this one included. */
const WEEKS = 8;

/**
 * The home screen's recent days (CR-34): two weeks back and one ahead, at this branch.
 *
 * The rest of the home screen is today. This is the context today sits in — is this a quiet week
 * or a busy one, is money coming in as usual, are people turning up — because a day's numbers on
 * their own cannot say whether they are good. Three charts, each the same figure the screen it
 * links to shows: appointments by day from the appointments summary (CR-33), what was sold by day
 * from the money summary (CR-34), and the no-show rate by week, Saturday to Friday, from the
 * appointments again. One request each for appointments and money, not one per chart.
 *
 * Each part waits for the screen it summarises to be released, as the rest of Home does.
 */
export function HomeTrends({
  clinicId,
  branchId,
  today,
  showAppointments,
  showSales,
}: {
  readonly clinicId: string;
  readonly branchId: string;
  readonly today: string;
  readonly showAppointments: boolean;
  readonly showSales: boolean;
}) {
  const { t, i18n } = useTranslation();

  // Eight weeks back (for the no-show trend) to a week ahead (for what is booked), in one call.
  const weeksFrom = weekStart(shiftDay(today, -(WEEKS - 1) * 7));
  const appointmentSearch = `from=${weeksFrom}&to=${shiftDay(today, DAYS_AHEAD)}&branchId=${branchId}`;
  const appointments = useApiQuery<AppointmentSummaryResponse>(
    queryKeys.appointmentSummary(clinicId, appointmentSearch),
    `/clinics/${clinicId}/appointments/summary?${appointmentSearch}`,
    { enabled: showAppointments },
  );
  const salesSearch = `from=${shiftDay(today, -(DAYS_BACK - 1))}&to=${today}&branchId=${branchId}`;
  const sales = useApiQuery<SalesSummaryResponse>(
    queryKeys.salesSummary(clinicId, salesSearch),
    `/clinics/${clinicId}/sales/summary?${salesSearch}`,
    { enabled: showSales },
  );

  if (!showAppointments && !showSales) {
    return null;
  }

  const weekOf = (first: string, last: string) => t('overview.weekOf', { from: first, to: last });
  const firstDay = shiftDay(today, -(DAYS_BACK - 1));

  const appointmentColumns: Column[] = (appointments.data?.byDay ?? [])
    .filter((day) => (day.date ?? '') >= firstDay)
    .map((day) => {
      const span = { start: day.date ?? '', end: day.date ?? '' };
      const lost = (day.noShow ?? 0) + (day.cancelled ?? 0);
      return {
        key: span.start,
        label: span.start === today ? t('overview.home.today') : bucketLabel(span, 'day', i18n.language),
        description: t('overview.columnDescription', {
          when: bucketTitle(span, 'day', i18n.language, weekOf),
          count: day.total ?? 0,
          completed: day.completed ?? 0,
          open: day.open ?? 0,
          lost,
        }),
        segments: [
          { value: day.completed ?? 0, colour: OUTCOME_COLOURS.completed },
          { value: day.open ?? 0, colour: OUTCOME_COLOURS.open },
          { value: lost, colour: OUTCOME_COLOURS.lost },
        ],
      };
    });

  const weeks = bucketDays(
    (appointments.data?.byDay ?? []).filter((day) => (day.date ?? '') <= today),
    'week',
  );
  const noShowColumns: Column[] = weeks.map((week) => {
    const due = week.completed + week.noShow;
    const rate = percent(week.noShow, due);
    return {
      key: week.start,
      label: bucketLabel(week, 'week', i18n.language),
      description:
        due === 0
          ? t('overview.home.noShowNoneDue', { when: bucketTitle(week, 'week', i18n.language, weekOf) })
          : t('overview.home.noShowWeek', {
              when: bucketTitle(week, 'week', i18n.language, weekOf),
              rate,
              noShows: week.noShow,
              due,
            }),
      segments: [{ value: rate, colour: OUTCOME_COLOURS.lost }],
    };
  });

  const salesColumns: Column[] = (sales.data?.byDay ?? []).map((day) => {
    const span = { start: day.day ?? '', end: day.day ?? '' };
    return {
      key: span.start,
      label: span.start === today ? t('overview.home.today') : bucketLabel(span, 'day', i18n.language),
      description: t('overview.sales.columnDescription', {
        when: bucketTitle(span, 'day', i18n.language, weekOf),
        sold: pounds(magnitude(day.sold)),
        count: day.saleCount ?? 0,
        taken: pounds(magnitude(day.taken)),
        refunded: pounds(magnitude(day.refunded)),
      }),
      segments: [{ value: magnitude(day.sold), colour: 'var(--chart-1)' }],
    };
  });

  // Nothing until there is something to draw: a heading over empty space reads as broken, and
  // this sits at the foot of the page, where arriving late moves nothing else.
  const anything =
    (showAppointments && appointmentColumns.length > 0) || (showSales && salesColumns.length > 0);
  if (!anything) {
    return null;
  }

  return (
    <section className="overview home-trends" aria-label={t('overview.home.heading')}>
      <h2 className="section__title">{t('overview.home.heading')}</h2>
      <div className="overview-grid">
        {showAppointments && appointmentColumns.length > 0 ? (
          <section className="panel">
            <div className="panel__header">
              <div className="panel__heading">
                <h2>{t('overview.home.appointments')}</h2>
                <p className="muted">{t('overview.home.appointmentsNote')}</p>
              </div>
              <Link className="button button--ghost button--compact" to="/appointments">
                {t('overview.home.openAppointments')}
                <Icon name="arrowEnd" size={18} />
              </Link>
            </div>
            <Columns columns={appointmentColumns} label={t('overview.home.appointments')} current={today} />
            <OutcomeLegend />
          </section>
        ) : null}

        {showSales && salesColumns.length > 0 ? (
          <section className="panel">
            <div className="panel__header">
              <div className="panel__heading">
                <h2>{t('overview.home.sales')}</h2>
                <p className="muted">{t('overview.home.salesNote')}</p>
              </div>
              <Link className="button button--ghost button--compact" to="/sales">
                {t('overview.home.openSales')}
                <Icon name="arrowEnd" size={18} />
              </Link>
            </div>
            <Columns
              columns={salesColumns}
              label={t('overview.home.sales')}
              formatTick={compact}
              current={today}
            />
          </section>
        ) : null}

        {showAppointments && noShowColumns.length > 0 ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.home.noShows')}</h2>
              <p className="muted">{t('overview.home.noShowsNote')}</p>
            </div>
            <Columns
              columns={noShowColumns}
              label={t('overview.home.noShows')}
              formatTick={(value) => `${Math.round(value)}%`}
              current={weeks[weeks.length - 1]?.start}
            />
          </section>
        ) : null}
      </div>
    </section>
  );
}

/** The Saturday a day's week began on (ASM-004), so the trend's first week is a whole one. */
function weekStart(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  const sinceSaturday = (date.getUTCDay() + 1) % 7;
  return shiftDay(isoDate, -sinceSaturday);
}
